import { Link } from "@tanstack/react-router";
import { ChevronRight, AlertTriangle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import type { ContentFlag } from "@/routes/innovation-days-demo/types";

interface ContentFlaggingContentProps {
  flags: ContentFlag[];
}

export function ContentFlaggingContent({ flags }: ContentFlaggingContentProps) {
  // Group flags by category
  const flagsByCategory = flags.reduce(
    (acc, flag) => {
      flag.categories.forEach((category) => {
        if (!acc[category]) {
          acc[category] = [];
        }
        acc[category].push(flag);
      });
      return acc;
    },
    {} as Record<string, ContentFlag[]>,
  );

  return (
    <div className="space-y-6">
      {Object.entries(flagsByCategory).map(([category, categoryFlags]) => (
        <div key={category} className="relative pl-4">
          {/* Left accent bar */}
          <div
            className="absolute left-0 top-0 bottom-0 w-1 rounded-full"
            style={{ background: "#F59E0B" }}
          />

          {/* Category header */}
          <div className="mb-4">
            <div className="flex items-center gap-2.5">
              <AlertTriangle
                className="w-3.5 h-3.5"
                style={{ color: "#F59E0B" }}
              />
              <h4
                className="text-[14px] leading-[20px] font-semibold"
                style={{ color: "#0F172A" }}
              >
                {category}
              </h4>
              <Badge
                className="w-auto px-2 py-0.5 rounded-full border-transparent text-[11px] leading-[16px] font-medium"
                style={{ background: "#FEF3C7", color: "#D97706" }}
              >
                {categoryFlags.length}{" "}
                {categoryFlags.length === 1 ? "Flag" : "Flags"}
              </Badge>
            </div>
          </div>

          {/* Flags list */}
          <div className="space-y-1.5">
            {categoryFlags.map((flag) => {
              const globalIndex = flags.indexOf(flag);
              return (
                <Link
                  key={`${category}-${globalIndex}-${flag.short_description}`}
                  to="/innovation-days-demo/viewer"
                  search={{
                    annotationType: "content_flag",
                    annotationIndex: globalIndex,
                  }}
                  className="group flex items-center gap-3 py-2.5 rounded-lg cursor-pointer transition-colors"
                >
                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <p
                      className="text-[14px] leading-[20px] group-hover:text-[#D97706] transition-colors"
                      style={{ color: "#475569" }}
                    >
                      {flag.short_description}
                    </p>
                  </div>

                  {/* Arrow with label */}
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <span
                      className="text-[12px] font-medium opacity-0 group-hover:opacity-100 transition-opacity hidden sm:block"
                      style={{ color: "#D97706" }}
                    >
                      View in Text
                    </span>
                    <ChevronRight
                      className="w-4 h-4 transition-transform group-hover:translate-x-0.5"
                      style={{ color: "#D97706" }}
                    />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
